import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { HugeiconsIconComponent, IconSvgObject } from '@hugeicons/angular';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-escala-historico',
  standalone: true,
  imports: [CommonModule, HugeiconsIconComponent, DialogModule, SkeletonModule, TooltipModule],
  templateUrl: './escala-historico.component.html',
  styleUrl: './escala-historico.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EscalaHistoricoComponent {
  @Input() items: any[] = [];
  @Input() loading = false;
  @Input() compact = false;
  @Input() emptyText = 'Ainda não existem ocorrências nesta escala.';
  @Input() emptyStateHugeIcon: IconSvgObject | null = null;
  @Input() occurrenceHugeIcon: IconSvgObject | null = null;

  imagemAberta: string | null = null;
  imagemVisible = false;
  imagemTitulo = '';

  get totalAbertas(): number {
    return this.items.filter((item) =>
      this.normalize(item?.estado).includes('ABERTA'),
    ).length;
  }

  get totalCriticas(): number {
    return this.items.filter((item) => {
      const value = this.normalize(item?.criticidade);
      return value.includes('ALTA') || value.includes('URGENTE');
    }).length;
  }

  get totalRondas(): number {
    return new Set(
      this.items.map((item) => item?.rondaId).filter((id) => id !== null && id !== undefined),
    ).size;
  }

  getTipo(item: any): string {
    return String(
      item?.tipoOcorrencia ?? item?.tipo ?? item?.descricaoTipo ?? 'Ocorrência',
    ).replace(/_/g, ' ');
  }

  getDescricao(item: any): string {
    return String(
      item?.descricaoTipoOutro ??
        item?.descricao ??
        item?.motivo ??
        'Sem descrição complementar.',
    );
  }

  getCriticidade(item: any): string {
    return String(item?.criticidade ?? 'Normal');
  }

  getCriticidadeClass(item: any): string {
    const value = this.normalize(item?.criticidade);
    if (value.includes('URGENTE')) return 'urgente';
    if (value.includes('ALTA')) return 'alta';
    if (value.includes('MEDIA')) return 'media';
    return 'baixa';
  }

  getEstadoClass(item: any): string {
    const value = this.normalize(item?.estado);
    if (value.includes('FECH') || value.includes('RESOLV')) return 'resolvida';
    if (value.includes('ANDAMENTO') || value.includes('TRATAMENTO')) return 'andamento';
    return 'aberta';
  }

  getImagens(item: any): string[] {
    const imagens = Array.isArray(item?.imagens) ? item.imagens : [];
    return imagens
      .map((imagem: any) => {
        if (typeof imagem === 'string') return imagem;
        return (
          imagem?.url ??
          imagem?.urlImagem ??
          imagem?.caminho ??
          imagem?.arquivoUrl ??
          imagem?.link ??
          null
        );
      })
      .filter(Boolean) as string[];
  }

  abrirImagem(url: string, item: any): void {
    this.imagemAberta = url;
    this.imagemVisible = true;
    this.imagemTitulo = `${this.getTipo(item)} · Ocorrência #${item?.id ?? '--'}`;
  }

  formatarData(value: unknown): string {
    const date = this.parseDate(value);
    if (!date) return '--';
    return new Intl.DateTimeFormat('pt-AO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  getRondaPeriodo(item: any): string {
    const inicio = this.parseDate(item?.ronda?.dataInicio);
    const fim = this.parseDate(item?.ronda?.dataFim);
    if (!inicio) return 'Horário da ronda indisponível';
    const inicioLabel = this.formatarHora(inicio);
    return fim ? `${inicioLabel}–${this.formatarHora(fim)}` : `${inicioLabel}–em curso`;
  }

  getRondaDuracao(item: any): string {
    const inicio = this.parseDate(item?.ronda?.dataInicio);
    const fim = this.parseDate(item?.ronda?.dataFim);
    if (!inicio || !fim) return '';
    const minutos = Math.max(0, Math.round((fim.getTime() - inicio.getTime()) / 60_000));
    const horas = Math.floor(minutos / 60);
    const resto = minutos % 60;
    return horas ? `${horas}h ${resto}min` : `${resto}min`;
  }

  trackItem(index: number, item: any): number | string {
    return item?.id ?? index;
  }

  private parseDate(value: unknown): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private formatarHora(date: Date): string {
    return date.toLocaleTimeString('pt-AO', { hour: '2-digit', minute: '2-digit' });
  }

  private normalize(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  }
}
